import { useState } from 'react';
import type { Track, TrackBlend } from '../../format/types';
import { TRACK_BLENDS } from '../../format/types';
import { useTimeline } from '../../state/timeline';
import ImportEditDialog from './ImportEditDialog';

export function TrackHeader({
  track,
  index,
  tracks,
}: {
  track: Track;
  index: number;
  tracks: Track[];
}) {
  const targeted = useTimeline((s) => s.targetTrack[track.kind] === track.id);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(track.label);
  const [importing, setImporting] = useState(false);
  const tl = useTimeline.getState();

  const canUp = index > 0 && tracks[index - 1].kind === track.kind;
  const canDown = index < tracks.length - 1 && tracks[index + 1].kind === track.kind;

  function commitRename() {
    const label = draft.trim();
    if (label && label !== track.label) tl.renameTrack(track.id, label);
    setRenaming(false);
  }

  function onRemove(e: React.MouseEvent) {
    e.stopPropagation();
    if (
      track.clips.length > 0 &&
      !window.confirm(`Remove track ${track.label} and its ${track.clips.length} clip(s)?`)
    )
      return;
    tl.removeTrack(track.id);
  }

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div
      className={`tl-label tl-label-${track.kind} ${targeted ? 'targeted' : ''}`}
      title="Click to target this track for new clips"
      onClick={() => tl.setTargetTrack(track.kind, track.id)}
    >
      <div className="tl-label-row">
        {renaming ? (
          <input
            className="tl-rename"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') commitRename();
              else if (e.key === 'Escape') setRenaming(false);
            }}
            onClick={stop}
            onPointerDown={stop}
          />
        ) : (
          <span
            className="tl-label-name"
            title="Double-click to rename"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setDraft(track.label);
              setRenaming(true);
            }}
          >
            {track.label}
          </span>
        )}
        <button
          className={`ghost icon tl-mute ${track.muted ? 'muted' : ''}`}
          title={track.muted ? 'Unmute track' : 'Mute track'}
          onClick={(e) => {
            e.stopPropagation();
            tl.setTrackMuted(track.id, !track.muted);
          }}
        >
          {track.muted ? '🔇' : '🔊'}
        </button>
        <button
          className={`ghost icon tl-solo ${track.solo ? 'active' : ''}`}
          title={track.solo ? 'Unsolo track (audio)' : 'Solo track (audio)'}
          onClick={(e) => {
            e.stopPropagation();
            tl.setTrackSolo(track.id, !track.solo);
          }}
        >
          S
        </button>
        <button
          className="ghost icon tl-track-remove"
          title="Remove track"
          onClick={onRemove}
        >
          ×
        </button>
      </div>
      <div className="tl-label-row tl-label-tools">
        <button
          className="ghost icon tl-track-move"
          title="Move track up"
          disabled={!canUp}
          onClick={(e) => {
            e.stopPropagation();
            tl.moveTrack(track.id, -1);
          }}
        >
          ▲
        </button>
        <button
          className="ghost icon tl-track-move"
          title="Move track down"
          disabled={!canDown}
          onClick={(e) => {
            e.stopPropagation();
            tl.moveTrack(track.id, 1);
          }}
        >
          ▼
        </button>
        {track.kind === 'audio' && (
          <input
            className="tl-mini-slider"
            type="range"
            min={0}
            max={2}
            step={0.05}
            value={track.gain ?? 1}
            title={`Track gain ${(track.gain ?? 1).toFixed(2)}`}
            onChange={(e) => tl.setTrackGain(track.id, Number(e.target.value))}
            onClick={stop}
            onPointerDown={stop}
          />
        )}
        {track.kind === 'video' && (
          <>
            <button
              className="ghost icon tl-track-import"
              title="Import a pan.do/ra edit onto this track at the playhead"
              onClick={(e) => {
                e.stopPropagation();
                setImporting(true);
              }}
              onPointerDown={stop}
            >
              ⤵
            </button>
            <select
              className="tl-blend-select"
              value={track.blend ?? 'normal'}
              title="Track blend mode"
              onChange={(e) =>
                tl.setTrackBlend(
                  track.id,
                  e.target.value === 'normal' ? null : (e.target.value as TrackBlend),
                )
              }
              onClick={stop}
              onPointerDown={stop}
            >
              <option value="normal">normal</option>
              {TRACK_BLENDS.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
            <input
              className="tl-mini-slider"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={track.opacity ?? 1}
              title={`Track opacity ${(track.opacity ?? 1).toFixed(2)}`}
              onChange={(e) => tl.setTrackOpacity(track.id, Number(e.target.value))}
              onClick={stop}
              onPointerDown={stop}
            />
          </>
        )}
      </div>
      {importing && (
        <ImportEditDialog trackId={track.id} onClose={() => setImporting(false)} />
      )}
    </div>
  );
}
